import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { AddCollaboratorsComponent } from '../add-collaborators/add-collaborators.component';
import { ColloboraterSettingsComponent } from '../colloborater-settings/colloborater-settings.component';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
  ConfirmationDialogResult,
} from '../confirmation-dialog/confirmation-dialog.component';
import { Collaborator } from '../../../modules/wizard/models/collaborator.model';
import { CollaboratorsInvitationModel } from 'app/modules/wizard/models/collaborators-invitation.model';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, take, takeUntil, tap, map } from 'rxjs/operators';
import { FetchCustomerOrgDetails } from '../../store/customer-org-details/customer-org-details.actions';
import { CustomerOrgDetailsState } from '../../store/customer-org-details/customer-org-details.state';
import { CollaboratorsState } from '../../store/collaborators/collaborators.state';
import { FetchCollaboratorData } from '../../store/collaborators/collaborators.actions';
import { EntityMap } from '@shared/models/types';
import { OrgUser } from 'app/modules/organization/models/org-user';

/** Job file `status` once the job has been terminated (set by ms-jobfile TerminateJob). */
export const JOB_FILE_TERMINATED_STATUS = 'TERMINATE';

/**
 * Message to show when POST /api/jpm/terminate-job fails. The backend returns a plain-text reason
 * (e.g. "Cannot find the job ... to terminate") on a 400; fall back to a generic message otherwise.
 */
export function terminateJobErrorMessage(
  error: unknown,
  fallback = 'Failed to terminate the job. Please try again.',
): string {
  const body = (error as { error?: unknown })?.error;
  return typeof body === 'string' && body.trim().length > 0
    ? body
    : fallback;
}

/** Toast for a planning-stage Terminate Job: says which loads were kept when some were shared. */
export function terminateJobSuccessMessage(result: { skippedLoadIds?: string[] } | null | undefined): string {
  const skipped = result?.skippedLoadIds ?? [];
  if (skipped.length === 0) {
    return 'Job terminated successfully';
  }
  return `Loads terminated. ${skipped.join(', ')} ${skipped.length === 1 ? 'was' : 'were'} sent to another `
    + 'organization by a service request or RFQ and kept, so the job stays open.';
}

@Component({
  selector: 'hmt-job-details-header',
  templateUrl: './job-details-header.component.html',
  standalone: false,
  styleUrls: ['./job-details-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobDetailsHeaderComponent implements OnInit, OnDestroy, OnChanges {
  private readonly destroy$ = new Subject<void>();
  
  @Select(CustomerOrgDetailsState.getCustomerOrgDetails) customerOrgDetails$!: Observable<EntityMap<string, { logo: string; orgName: string }>>;
  @Select(CollaboratorsState.getCollaboratorPhotos) collaboratorPhotos$!: Observable<EntityMap<string, string>>;
  @Select(CollaboratorsState.getCollaboratorData) collaboratorData$!: Observable<EntityMap<string, OrgUser>>;
  
  customerLogo: string | null = null;
  collaboratorsWithPhotos$!: Observable<Array<{ name: string; avatarUrl: string }>>;
  extraCollaboratorCount$!: Observable<number>;
  
  constructor(
    private dialog: MatDialog,
    private store: Store,
    private cdr: ChangeDetectorRef
  ) {}
  @Input() workflow: string = 'Distribution';
  @Input() jobName: string = 'Test-Job 01';
  @Input() jobRef: string = 'JLFD-34355';
  @Input() title: string = 'Job Details';
  @Input() customer: { orgName: string; orgId?: string; id?: string } | null = null;
  @Output() inviteCollaborators = new EventEmitter<CollaboratorsInvitationModel>();
  @Output() removeCollaborator = new EventEmitter<string>();

  @Input() collaborators: Array<{ name: string; avatarUrl: string }> = [
    { name: 'James Lee', avatarUrl: 'assets/images/no_profile_image.png' },
    { name: 'Charles Dean', avatarUrl: 'assets/images/no_profile_image.png' },
    { name: 'James Lee', avatarUrl: 'assets/images/no_profile_image.png' },
  ];
  @Input() collaboratorsFull: Collaborator[] = [];

  @Input() extraCollaboratorCount: number = 5;

  @Input() purpleBand: boolean = false;
  @Input() showManualPlanning: boolean = false;
  @Input() showBack: boolean = true;

  @Output() back = new EventEmitter<void>();
  @Output() manualPlanning = new EventEmitter<void>();

  /** Show the ⋮ Job Actions menu next to the Job Reference No. */
  @Input() showJobActions = false;
  /** Disable the Job Actions menu, e.g. once the job is terminated. */
  @Input() jobActionsDisabled = false;
  /** Tooltip on the disabled Job Actions button, saying why it is disabled. */
  @Input() jobActionsDisabledReason: string | null = null;
  /** Emitted after the user confirms Terminate Job. */
  @Output() terminateJob = new EventEmitter<void>();

  onTerminateJobClick(): void {
    this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData, ConfirmationDialogResult>(
      ConfirmationDialogComponent,
      {
        width: '400px',
        data: {
          message: 'Are you sure you want to terminate?',
          imagePath: 'assets/icons/terminate-job.svg',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          variant: 'danger',
        },
      },
    ).afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result?.buttonPressed === 'yes') {
          this.terminateJob.emit();
        }
      });
  }


  onBack(): void {
    this.back.emit();
  }

  onManualPlanning(): void {
    this.manualPlanning.emit();
  }

  onAddCollaborator(): void {
    const dialogRef = this.dialog.open(ColloboraterSettingsComponent, {
      width: '60%',
      data: {
        collaborators: this.collaboratorsFull,
        jobRefId: this.jobRef
      }
    });

    const inviteSubscription = dialogRef.componentInstance.inviteCollaboratorsEmitter
      .subscribe((collaboratorsInvitationModel: CollaboratorsInvitationModel) => {
        this.inviteCollaborators.emit(collaboratorsInvitationModel);
        dialogRef.close();
        inviteSubscription.unsubscribe();
      });

    const removeSubscription = dialogRef.componentInstance.removeCollaboratorEmitter
      .subscribe((collaboratorUserId: string) => {
        this.removeCollaborator.emit(collaboratorUserId);
      });

    dialogRef.afterClosed().subscribe(() => {
      removeSubscription.unsubscribe();
    });
  }

  getWorkflowName(workflow: string): string {
    console.log(workflow);
    if (workflow === 'LF-DI-FTL') {
      return 'Distribution';
    }
    if (workflow === 'LF-DO-FTL') {
      return 'Domestic';
    }
    return workflow;
  }

  checkLogoIcon(workflow: string){
    if(workflow.includes('DI')) {
      return true;
    }
    return false;
  }

  ngOnInit(): void {
    // Subscribe to customerOrgDetails to get customer logo when fetched
    this.customerOrgDetails$
      .pipe(takeUntil(this.destroy$))
      .subscribe(details => {
        if (this.customer?.orgId && details[this.customer.orgId]) {
          this.customerLogo = details[this.customer.orgId].logo;
          this.cdr.markForCheck();
        }
      });
    
    // Initial fetch if customer is already set
    if (this.customer?.orgId) {
      this.fetchCustomerLogo();
    }

    // Fetch collaborator data and photos on init
    this.fetchCollaboratorData();
    
    // Initialize collaboratorsWithPhotos$ observable
    this.updateCollaboratorsWithPhotos();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When customer changes, fetch logo if orgId is available
    if (changes['customer'] && this.customer?.orgId) {
      this.fetchCustomerLogo();
    } else if (changes['customer'] && !this.customer) {
      // Customer removed, clear logo
      this.customerLogo = null;
      this.cdr.markForCheck();
    }

    // When collaboratorsFull changes, fetch data and update photos
    if (changes['collaboratorsFull']) {
      this.fetchCollaboratorData();
      this.updateCollaboratorsWithPhotos();
    }

    // When collaborators change, update the observable
    if (changes['collaborators']) {
      this.updateCollaboratorsWithPhotos();
    }
  }

  private fetchCustomerLogo(): void {
    if (!this.customer?.orgId) {
      return;
    }

    const state = this.store.selectSnapshot(CustomerOrgDetailsState.getCustomerOrgDetails);
    
    // Check if already fetched
    if (state[this.customer.orgId]) {
      // Already fetched, use cached value
      this.customerLogo = state[this.customer.orgId].logo;
      this.cdr.markForCheck();
    } else {
      // Fetch organization details (logo)
      this.store.dispatch(new FetchCustomerOrgDetails(this.customer.orgId));
      
      // Subscribe to get the logo once it's fetched
      this.customerOrgDetails$
        .pipe(
          filter(details => !!details[this.customer.orgId]),
          take(1),
          tap(details => {
            this.customerLogo = details[this.customer.orgId].logo;
            this.cdr.markForCheck();
          }),
          takeUntil(this.destroy$)
        )
        .subscribe();
    }
  }

  private fetchCollaboratorData(): void {
    if (!this.collaboratorsFull || this.collaboratorsFull.length === 0) {
      return;
    }

    // Extract user IDs from collaboratorsFull (Collaborator.user is the org_user_id)
    const collaboratorUserIds = this.collaboratorsFull
      .map(c => c.user)
      .filter(userId => userId != null && userId !== '');

    if (collaboratorUserIds.length > 0) {
      // Dispatch action to fetch collaborator data and photos
      this.store.dispatch(new FetchCollaboratorData(collaboratorUserIds));
    }
  }

  private updateCollaboratorsWithPhotos(): void {
    // Combine collaborator data and photos to get avatar URLs
    this.collaboratorsWithPhotos$ = combineLatest([
      this.collaboratorPhotos$,
      this.collaboratorData$
    ]).pipe(
      map(([photos, collaboratorData]) => {
        // Use collaboratorsFull if available, otherwise fall back to collaborators
        const sourceCollaborators = this.collaboratorsFull.length > 0
          ? this.collaboratorsFull.map(c => ({ name: c.name, userId: c.user }))
          : this.collaborators.map(c => ({ name: c.name, userId: null }));

        // Map to get photos
        return sourceCollaborators.map(c => {
          if (c.userId && collaboratorData[c.userId]) {
            // Get the OrgUser to find photo ID
            const orgUser = collaboratorData[c.userId];
            if (orgUser.photo && photos[orgUser.photo]) {
              return {
                name: c.name,
                avatarUrl: photos[orgUser.photo]
              };
            }
          }
          // Fallback: find in original collaborators array or use default
          const original = this.collaborators.find(orig => orig.name === c.name);
          return {
            name: c.name,
            avatarUrl: original?.avatarUrl || 'assets/images/no_profile_image.png'
          };
        });
      }),
      takeUntil(this.destroy$)
    );

    // Calculate extra collaborator count (total - 2, minimum 0)
    this.extraCollaboratorCount$ = this.collaboratorsWithPhotos$.pipe(
      map(collaborators => Math.max(0, collaborators.length - 2)),
      takeUntil(this.destroy$)
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
